import React, { useState } from 'react';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import EditIcon from '@mui/icons-material/Edit';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import BarChartOutlinedIcon from '@mui/icons-material/BarChartOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { styled, ThemeProvider, createTheme } from '@mui/material/styles';
import darssyTheme from '../theme/darssyTheme';
import { collection, query, where, getDocs, updateDoc, doc, getDoc, setDoc, serverTimestamp, runTransaction } from 'firebase/firestore';
import { auth } from '../firebaseConfig';
import { createUserWithEmailAndPassword, signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { db } from '../firebaseConfig';
import logo from '../img/logo.png';
import studentLoginLock from '../img/StudentLoginLock.png';
import { useNavigate } from 'react-router-dom';
import { UAParser } from 'ua-parser-js';
import { useTranslation } from 'react-i18next';
import { setStoredTutorType } from '../utils/roleTerminology';

// Scoped to this page only: layers an elegant font pairing on top of the
// shared darssyTheme without touching the rest of the student portal.
// MUI bakes `fontFamily` into every typography variant at creation time,
// so overriding just typography.fontFamily silently does nothing — each
// variant (h1-h6, body1/2, button...) needs the override explicitly.
// Playfair Display (an elegant high-contrast serif) is used for headings only —
// a full page set in a display serif hurts legibility on form labels/inputs,
// so body copy, labels, and buttons keep a clean sans stack.
const HEADING_FONT_STACK = '"Playfair Display", "Space Grotesk", Georgia, serif';
const BODY_FONT_STACK = '"Plus Jakarta Sans", "Work Sans", system-ui, sans-serif';
const loginTheme = createTheme(darssyTheme, {
  typography: {
    fontFamily: BODY_FONT_STACK,
    h1: { fontFamily: HEADING_FONT_STACK },
    h2: { fontFamily: HEADING_FONT_STACK },
    h3: { fontFamily: HEADING_FONT_STACK },
    h4: { fontFamily: HEADING_FONT_STACK },
    h5: { fontFamily: HEADING_FONT_STACK },
    h6: { fontFamily: HEADING_FONT_STACK },
    subtitle1: { fontFamily: BODY_FONT_STACK },
    subtitle2: { fontFamily: BODY_FONT_STACK },
    body1: { fontFamily: BODY_FONT_STACK },
    body2: { fontFamily: BODY_FONT_STACK },
    button: { fontFamily: BODY_FONT_STACK },
    caption: { fontFamily: BODY_FONT_STACK },
    overline: { fontFamily: BODY_FONT_STACK },
  },
});

// CSS styles
const CenteredContainer = styled(Container)({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '100vh',
});

const Form = styled('form')(({ theme }) => ({
  width: '100%', 
  marginTop: theme.spacing(1),
  boxSizing: 'border-box', 
}));

const PaperContainer = styled(Paper)(({ theme }) => ({
  margin: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(6, 5.5),
  borderRadius: '18px',
  width: '90%',
  maxWidth: '460px',
  boxSizing: 'border-box',
}));

const outlinedFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    backgroundColor: '#fff',
    fontSize: '13.5px',
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' },
  '& .Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#2f6fed', borderWidth: '1.5px' },
  '& .MuiOutlinedInput-input': { padding: '9px 10px' },
  '& .MuiInputAdornment-root .MuiSvgIcon-root': { color: '#94a3b8', fontSize: 17 },
};

const LogoImage = styled('img')({
  width: '300px',
  height: 'auto',
});

const AvatarIcon = styled(Avatar)(({ theme }) => ({
  margin: theme.spacing(1),
  backgroundColor: theme.palette.secondary.main,
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(2, 0, 1),
}));



export default function SignIn() {

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [allowForgotPassword, setAllowForgotPassword] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [fullyRegistered, setFullyRegistered] = useState(null);
  const [studentDocId, setStudentDocId] = useState(null);
  const [rememberMe, setRememberMe] = useState(true);
  const [langMenuAnchor, setLangMenuAnchor] = useState(null);

    // ===== Language switcher (top-right) =====
    const LANGS = [
      { code: "ar", label: "AR" },
      { code: "fr", label: "FR" },
      { code: "en", label: "EN" },
    ];

    const handleChangeLanguage = (langCode) => {
      i18n.changeLanguage(langCode);
      // Optional persistence (recommended if you want it remembered on refresh)
      localStorage.setItem("i18nextLng", langCode);
      setLangMenuAnchor(null);
    };

  const isRtl = i18n.dir(i18n.resolvedLanguage || i18n.language) === "rtl";
  const fieldDirection = isRtl ? "rtl" : "ltr";
  const authFieldSx = {
    direction: fieldDirection,
    "& input": {
      textAlign: isRtl ? "right" : "left",
    },
  };
  const activeLang = LANGS.find((l) => i18n.language?.startsWith(l.code)) || LANGS[2];



  React.useEffect(() => {
    // Show "Forgot Password" only when we want to LOGIN:
    if (fullyRegistered === true) {
      setAllowForgotPassword(true);
    } else {
      setAllowForgotPassword(false);
    }
  }, [fullyRegistered]);
  


  // ===== Device helpers (Composite fingerprint: UA Client Hints + UA parser + features) =====

  // Small helper: SHA-256 hash -> hex string
  async function hashString(str) {
    const data = new TextEncoder().encode(str);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hashBuffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  // Build a composite fingerprint and hash it to get a stable deviceId.
  // Tries UA Client Hints first, falls back to UA string via ua-parser-js, and adds stable features.
  async function getCompositeDeviceId() {
    let uaHints = {};
    if (navigator.userAgentData?.getHighEntropyValues) {
      try {
        uaHints = await navigator.userAgentData.getHighEntropyValues([
          'architecture',
          'bitness',
          'model',
          'platform',
          'platformVersion',
          'uaFullVersion',
          'fullVersionList'
        ]);
      } catch (_) {
        // ignore and fallback to UA parsing
      }
    }

    const parser = new UAParser();
    const ua = parser.getResult();

    const features = {
      // Client Hints (if available)
      ch_platform: uaHints.platform || '',
      ch_platformVersion: uaHints.platformVersion || '',
      ch_model: uaHints.model || '',
      ch_arch: uaHints.architecture || '',
      ch_bitness: uaHints.bitness || '',
      ch_fullVersion: uaHints.uaFullVersion || '',
      ch_brands: Array.isArray(uaHints.fullVersionList)
        ? uaHints.fullVersionList.map(b => `${b.brand}/${b.version}`).join('|')
        : '',

      // UA parser fallback (browser + os)
      ua_browser: `${ua.browser.name || ''}/${ua.browser.version || ''}`,
      ua_os: `${ua.os.name || ''}/${ua.os.version || ''}`,

      // Stable device traits
      screen: `${window.screen?.width || 0}x${window.screen?.height || 0}@${window.devicePixelRatio || 1}`,
      colorDepth: window.screen?.colorDepth || 0,
      lang: navigator.language || '',
      langs: Array.isArray(navigator.languages) ? navigator.languages.join(',') : '',
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
      tzOffset: new Date().getTimezoneOffset() || 0,
      cores: navigator.hardwareConcurrency || 0,
      mem: navigator.deviceMemory || 0,
      touch: navigator.maxTouchPoints || 0,
      platform: navigator.platform || '',
      // Browser feature presence can also stabilize the hash a bit
      storages: [
        !!window.indexedDB,
        !!window.localStorage,
        !!window.sessionStorage,
      ].join(','),
    };

    const fpString = JSON.stringify(features);
    return await hashString(fpString);
  }

  // Firestore: Devices/{uid}
  function devicesDocRef(uid) {
    return doc(db, 'Devices', uid);
  }

  // Register the first device into Devices/{uid} if the doc doesn't exist yet.
  async function registerFirstDevice(uid, deviceId) {
    const ref = devicesDocRef(uid);
    const snap = await getDoc(ref);

    const modelSummary = navigator.userAgent; // quick label for UI

    if (!snap.exists()) {
      await setDoc(ref, {
        devices: [deviceId],
        createdAt: serverTimestamp(),
        lastSeen: { [deviceId]: serverTimestamp() },
        models: { [deviceId]: modelSummary },
      });
    }
  }

  // Enforce: max 2 devices; auto-add as second if only 1 exists and different.
  // Throws Error('DEVICE_LIMIT') if trying to add a 3rd device.
  async function enforceDeviceLimitOnLogin(uid, deviceId) {
    const ref = devicesDocRef(uid);
    const modelSummary = navigator.userAgent;

    await runTransaction(db, async (tx) => {
      const snap = await tx.get(ref);

      // No record yet → create with this device
      if (!snap.exists()) {
        tx.set(ref, {
          devices: [deviceId],
          createdAt: serverTimestamp(),
          lastSeen: { [deviceId]: serverTimestamp() },
          models: { [deviceId]: modelSummary },
        });
        return;
      }

      const data = snap.data() || {};
      const devices = Array.isArray(data.devices) ? [...data.devices] : [];
      const has = devices.includes(deviceId);

      if (has) {
        const lastSeen = { ...(data.lastSeen || {}) };
        lastSeen[deviceId] = serverTimestamp();
        tx.update(ref, { lastSeen });
        return;
      }

      if (devices.length === 0 || devices.length === 1) {
        // Auto-save this as the second device
        devices.push(deviceId);
        const lastSeen = { ...(data.lastSeen || {}) };
        const models = { ...(data.models || {}) };
        lastSeen[deviceId] = serverTimestamp();
        models[deviceId] = modelSummary;

        tx.update(ref, { devices, lastSeen, models });
        return;
      }

      // Already have 2, and this is a third → block
      throw new Error('DEVICE_LIMIT');
    });

    return { ok: true };
  }


  // Helper to check if today is between start and end date
  function isMembershipActive(startDate, endDate) {
    if (!endDate || !startDate) return true;
    const today = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    return today >= start && today <= end;
  }

  // Redirect rule:
  // If student has exactly 1 enrollment and that class belongs to a Coach,
  // send directly to the class page. Otherwise go to classes list.
  const getStudentPostLoginRoute = async (uid, fallbackEmail = "") => {
    try {
      let studentSnap = await getDocs(
        query(collection(db, "students"), where("uid", "==", uid))
      );

      if (studentSnap.empty && fallbackEmail) {
        studentSnap = await getDocs(
          query(
            collection(db, "students"),
            where("email", "==", fallbackEmail.trim().toLowerCase())
          )
        );
      }

      if (studentSnap.empty) {
        return "/student/classes";
      }

      const studentData = studentSnap.docs[0].data() || {};
      const enrollments = Array.isArray(studentData.enrollments) ? studentData.enrollments : [];

      if (enrollments.length !== 1) {
        setStoredTutorType("Teacher");
        return "/student/classes";
      }

      const onlyEnrollment = enrollments[0] || {};
      const groupId = onlyEnrollment.groupId;
      if (!groupId) {
        setStoredTutorType("Teacher");
        return "/student/classes";
      }

      const groupRef = doc(db, "groups", groupId);
      const groupSnap = await getDoc(groupRef);
      if (!groupSnap.exists()) {
        setStoredTutorType("Teacher");
        return "/student/classes";
      }

      const teacherId = groupSnap.data()?.teacherId;
      if (!teacherId) {
        setStoredTutorType("Teacher");
        return "/student/classes";
      }

      const teacherRef = doc(db, "teachers", teacherId);
      const teacherSnap = await getDoc(teacherRef);
      if (!teacherSnap.exists()) {
        setStoredTutorType("Teacher");
        return "/student/classes";
      }

      const tutorType = String(teacherSnap.data()?.tutorType || "Teacher").trim().toLowerCase();
      setStoredTutorType(tutorType === "coach" ? "Coach" : "Teacher");
      if (tutorType === "coach") {
        return `/student/class/${groupId}`;
      }

      return "/student/classes";
    } catch (routeErr) {
      console.error("Error resolving post-login student route:", routeErr);
      setStoredTutorType("Teacher");
      return "/student/classes";
    }
  };

  const handleLogin = async () => {
    setError('');
    setSuccess('');

    try {
      const normalizedEmail = email.trim().toLowerCase();

      const studentsQuery = query(
        collection(db, 'students'),
        where('email', '==', normalizedEmail)
      );
      const querySnapshot = await getDocs(studentsQuery);

      if (querySnapshot.empty) {
        setError(t('لم نجد حساباً مفعّلاً بهذا البريد. إن كنت قد سجّلت عبر رمز QR الخاص بقسمك، فطلبك ينتظر موافقة أستاذك: بمجرد قبوله، سجّل الدخول بنفس البريد وكلمة السر — لا تنشئ حساباً جديداً.'));
        return;
      }

      const studentData = querySnapshot.docs[0].data();
      setStudentDocId(querySnapshot.docs[0].id);

      // ✅ Membership date check
      const startDate = studentData.startDate;
      const endDate = studentData.endDate;

      if (!isMembershipActive(startDate, endDate)) {
        setError(t('انتهت صلاحية اشتراكك. يرجى تجديد العضوية أولاً.'));
        console.log(endDate)
        return;
      }

      // Existing conditions
      if (studentData.disabled) {
        setError(t('تم تعطيل حسابك. يرجى التواصل مع الإدارة'));
        return;
      }

      if (!studentData.adminValidated) {
        setSuccess(t('! يرجى التوجه إلى إدارة المدرسة لتأكيد تسجيلك'));
      } else if (studentData.adminValidated && !studentData.fullyRegistered) {
        setFullyRegistered(false);
      } else if (studentData.adminValidated && studentData.fullyRegistered) {
        setFullyRegistered(true);
      }

    } catch (error) {
      console.error('Error checking email:', error);
      setError(t('حدث خطأ أثناء التحقق من البريد الإلكتروني'));
    }
  };
  
  

  const handleSubmit = async (e) => {
    setError('')
    e.preventDefault();
  
    try {

      // Normalize email to lowercase
      const normalizedEmail = email.trim().toLowerCase();

      if (fullyRegistered) {
        // Login
        const userCredential = await signInWithEmailAndPassword(auth, normalizedEmail, password);
        const uid = userCredential.user.uid;

        // Build composite deviceId
        // const deviceId = await getCompositeDeviceId();

        // try {
        //   await enforceDeviceLimitOnLogin(uid, deviceId);
        // } catch (e) {
        //   await auth.signOut();
        //   setError('لقد وصلت للحد الأقصى للأجهزة (2). الرجاء استخدام أحد أجهزتك المسجلة أو إزالة جهاز قديم.');
        //   return;
        // }

        setCurrentUser(userCredential.user);
        const destination = await getStudentPostLoginRoute(uid, normalizedEmail);
        navigate(destination);

      } else {
        // Register
        if (password !== confirmPassword) {
          setError(t('! كلمات السر غير متطابقة'));
          return;
        }
  
        // Create user in Firebase Auth
        const userCredential = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
        const { user } = userCredential;
        setCurrentUser(user);
        setSuccess(t('! تم التسجيل بنجاح'));
        //navigate('/student/classes');
  
        // Find the user in students collection based on email and phone
        const studentsQuery = query(
          collection(db, 'students'),
          where('email', '==', normalizedEmail)
        );
        const querySnapshot = await getDocs(studentsQuery);
  
        if (!querySnapshot.empty) {
          // Update the student's document with authID
          const studentDoc = querySnapshot.docs[0];
          const studentRef = doc(db, 'students', studentDoc.id);
  
          await updateDoc(studentRef, {
            uid: user.uid, 
            role: 'student',
            fullyRegistered: true, 
          });
  
          setStudentDocId(studentDoc.id);
          setFullyRegistered(true);
          // Save first device under Devices/{uid} using a device-bound ID
          const uid = user.uid;
          const deviceId = await getCompositeDeviceId();
          await registerFirstDevice(uid, deviceId);
          const destination = await getStudentPostLoginRoute(uid, normalizedEmail);
          navigate(destination);
        } else {
          // If no matching student is found
          setError(t('لم يتم العثور على طالب مطابق. يرجى التواصل مع الإدارة'));
        }
      }
    } catch (error) {
      console.error('Error during authentication:', error);
      if (error.code === 'auth/weak-password') {
        setError(t('كلمة السر ضعيفة جدًا. يرجى اختيار كلمة سر أقوى'));
      } else {
        setError(t('حدث خطأ أثناء العملية. يرجى المحاولة مرة أخرى'));
      }
    }
  };
  


  // Forgot password functionlaity: 
  const handleForgotPassword = async () => {
    setError('');     
    setSuccess('');   
  
    if (!email) {
      setError(t('يرجى إدخال البريد الإلكتروني لإعادة تعيين كلمة المرور.'));
      return;
    }
  
    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess(t('تم إرسال رسالة استعادة كلمة المرور إلى بريدك الإلكتروني.'));
    } catch (err) {
      console.error('Error sending reset email:', err);
      setError(t('حدث خطأ أثناء إرسال رسالة استعادة كلمة المرور. يرجى المحاولة لاحقًا.'));
    }
  };

  

  const FEATURE_BULLETS = [
    { icon: <GroupsOutlinedIcon fontSize="small" />, label: t('الانضمام إلى الصفوف') },
    { icon: <BarChartOutlinedIcon fontSize="small" />, label: t('متابعة تقدمك الدراسي') },
    { icon: <DescriptionOutlinedIcon fontSize="small" />, label: t('الاطلاع على الدروس والواجبات') },
  ];

  const renderFieldLabel = (text, htmlFor) => (
    <Typography
      component="label"
      htmlFor={htmlFor}
      sx={{
        display: 'block',
        fontSize: 12,
        fontWeight: 600,
        color: '#334155',
        mb: 0.5,
        textAlign: isRtl ? 'right' : 'left',
      }}
    >
      {text}
    </Typography>
  );

  return (
    <ThemeProvider theme={loginTheme}>
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        fontFamily: BODY_FONT_STACK,
        background: 'linear-gradient(180deg, #eef4ff 0%, #f8fafc 55%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <CssBaseline />

      {/* Soft decorative circle behind the logo */}
      <Box
        sx={{
          position: 'absolute',
          top: -180,
          left: -180,
          width: 420,
          height: 420,
          borderRadius: '50%',
          background: 'radial-gradient(circle at 35% 35%, rgba(59,111,237,0.18), rgba(59,111,237,0.04) 70%)',
          zIndex: 0,
        }}
      />

      {/* Logo — pinned to the top-left of the whole page */}
      <LogoImage
        src={logo}
        alt={t('Logo')}
        sx={{
          position: 'absolute',
          top: { xs: 20, md: 40 },
          left: { xs: 20, md: 56 },
          width: 140,
          zIndex: 2,
        }}
      />

      {/* Top-right language switcher — pinned to the whole page */}
      <Box sx={{ position: 'absolute', top: { xs: 16, md: 32 }, right: { xs: 16, md: 40 }, zIndex: 10 }}>
        <Button
          size="small"
          onClick={(e) => setLangMenuAnchor(e.currentTarget)}
          aria-label={t('اختر اللغة')}
          startIcon={<LanguageOutlinedIcon sx={{ color: '#2f6fed' }} />}
          endIcon={<KeyboardArrowDownIcon />}
          sx={{
            minWidth: 88,
            borderRadius: 999,
            fontWeight: 700,
            textTransform: 'none',
            bgcolor: '#fff',
            color: '#111827',
            border: '1px solid #e2e8f0',
            boxShadow: '0 2px 6px rgba(15,23,42,0.06)',
            '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' },
          }}
        >
          {activeLang.label}
        </Button>
        <Menu
          anchorEl={langMenuAnchor}
          open={Boolean(langMenuAnchor)}
          onClose={() => setLangMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {LANGS.map((l) => (
            <MenuItem
              key={l.code}
              selected={activeLang.code === l.code}
              onClick={() => handleChangeLanguage(l.code)}
            >
              {l.label}
            </MenuItem>
          ))}
        </Menu>
      </Box>

      {/* Left text column */}
      <Box
        sx={{
          flex: { xs: '0 0 auto', md: '0 0 42%' },
          minHeight: { xs: 'auto', sm: 260, md: '100vh' },
          p: { xs: 4, md: 8 },
          pt: { xs: 11, md: 8 },
          pb: { xs: 1, sm: 4, md: '100px', lg: '100px' },
          textAlign: { xs: 'center', sm: 'inherit' },
          alignItems: { xs: 'center', sm: 'stretch' },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Typography variant="h3" sx={{ fontWeight: 800, color: '#0f172a', mb: 1.5, fontSize: { xs: 32, md: 44 } }}>
          {t('مرحباً بعودتك')}
        </Typography>

        <Typography sx={{ color: '#475569', mb: { xs: 2, sm: 4 }, maxWidth: 380, fontSize: 16 }}>
          {t('أدر صفوفك ودروسك وواجباتك ورسائلك في مكان واحد.')}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', sm: 'flex-start' }, flexWrap: 'wrap', gap: 1.5 }}>
          {FEATURE_BULLETS.map((bullet, idx) => (
            <React.Fragment key={bullet.label}>
              {idx > 0 && (
                <Box sx={{ width: '1px', height: 20, bgcolor: '#cbd5e1', display: { xs: 'none', sm: 'block' } }} />
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#2f6fed' }}>
                {bullet.icon}
                <Typography sx={{ color: '#334155', fontWeight: 600, fontSize: 13.5 }}>{bullet.label}</Typography>
              </Box>
            </React.Fragment>
          ))}
        </Box>
      </Box>

      {/* Decorative shield/lock illustration */}
      <Box
        role="presentation"
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: { md: 320, lg: 380 },
          height: { md: 190, lg: 225 },
          display: { xs: 'none', md: 'block' },
          backgroundImage: `url(${studentLoginLock})`,
          backgroundSize: 'cover',
          backgroundPosition: 'left 62%',
          backgroundRepeat: 'no-repeat',
          WebkitMaskImage: 'linear-gradient(115deg, #000 45%, transparent 85%)',
          maskImage: 'linear-gradient(115deg, #000 45%, transparent 85%)',
          pointerEvents: 'none',
        }}
      />

      {/* Right form column */}
      <Box
        sx={{
          flex: { xs: '0 0 auto', sm: 1 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          p: 3,
          pt: { xs: 1, sm: 3 },
          zIndex: 1,
        }}
      >
        <CenteredContainer component="main" maxWidth="sm" sx={{ minHeight: 'auto' }}>
          <PaperContainer elevation={0} sx={{ mt: { xs: 1, sm: 4 }, border: '1px solid #eef1f6', boxShadow: '0 10px 24px rgba(15, 23, 42, 0.06)' }}>
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: '50%',
                bgcolor: '#eef4ff',
                color: '#2f6fed',
                display: 'grid',
                placeItems: 'center',
                mb: 2,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 24 }} />
            </Box>

            <Box sx={{ mb: { xs: 2, sm: 3.5 }, textAlign: "center" }}>
              <Typography
                component="h1"
                variant="h4"
                sx={{
                  fontWeight: 500,
                  color: "#0f172a",
                  mb: 0.5,
                  fontSize: { xs: 20, md: 32 },
                }}
              >
                {t('تسجيل دخول الطالب')}
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: "#64748b",
                  fontWeight: 400,
                  fontSize: 12.5,
                }}
              >
                {t('أدخل بياناتك للوصول إلى حسابك الدراسي')}
              </Typography>
            </Box>

          <Form noValidate onSubmit={handleSubmit} dir={fieldDirection}>
            <Box sx={{ mb: fullyRegistered !== null ? 0 : 1 }}>
              {renderFieldLabel(t('عنوان البريد الإلكتروني'), 'email')}
              <TextField
                variant="outlined"
                required
                fullWidth
                id="email"
                name="email"
                placeholder={t('أدخل بريدك الإلكتروني')}
                autoComplete="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                inputProps={{ dir: fieldDirection }}
                InputProps={{
                  readOnly: fullyRegistered !== null,
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailOutlineIcon />
                    </InputAdornment>
                  ),
                  endAdornment: fullyRegistered !== null ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        aria-label={t('تغيير البريد الإلكتروني')}
                        onClick={() => window.location.reload()}
                        edge="end"
                        sx={{ color: '#12344d' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
                sx={{ ...authFieldSx, ...outlinedFieldSx }}
              />
            </Box>

            {fullyRegistered !== null && (
              <>
                <Box sx={{ mt: 2.25 }}>
                  {renderFieldLabel(t('كلمة المرور'), 'password')}
                  <TextField
                    variant="outlined"
                    required
                    fullWidth
                    name="password"
                    placeholder={t('أدخل كلمة المرور')}
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    inputProps={{ dir: fieldDirection }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockOutlinedIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            aria-label={showPassword ? t('إخفاء كلمة المرور') : t('إظهار كلمة المرور')}
                            onClick={() => setShowPassword((prev) => !prev)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ ...authFieldSx, ...outlinedFieldSx }}
                  />
                </Box>

                {!fullyRegistered && (
                  <Box sx={{ mt: 2.25 }}>
                    {renderFieldLabel(t('تأكيد كلمة المرور'), 'confirmPassword')}
                    <TextField
                      variant="outlined"
                      required
                      fullWidth
                      name="confirmPassword"
                      placeholder={t('أعد إدخال كلمة المرور')}
                      type={showConfirmPassword ? 'text' : 'password'}
                      id="confirmPassword"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      inputProps={{ dir: fieldDirection }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <LockOutlinedIcon />
                          </InputAdornment>
                        ),
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              aria-label={showConfirmPassword ? t('إخفاء كلمة المرور') : t('إظهار كلمة المرور')}
                              onClick={() => setShowConfirmPassword((prev) => !prev)}
                              edge="end"
                            >
                              {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{ ...authFieldSx, ...outlinedFieldSx }}
                    />
                  </Box>
                )}

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mt: 1.5,
                    flexDirection: isRtl ? 'row-reverse' : 'row',
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        sx={{ color: '#cbd5e1', '&.Mui-checked': { color: '#2f6fed' } }}
                      />
                    }
                    label={<Typography sx={{ fontSize: 12.5, color: '#334155' }}>{t('تذكرني')}</Typography>}
                    sx={{ ml: 0, mr: 0 }}
                  />

                  {allowForgotPassword && (
                    <Link
                      variant="body2"
                      onClick={handleForgotPassword}
                      style={{ cursor: 'pointer', color: '#2f6fed', fontWeight: 600, fontSize: 12.5 }}
                    >
                      {t('نسيت كلمة المرور؟')}
                    </Link>
                  )}
                </Box>
              </>
            )}

            {error && (
              <Typography color="error" variant="body2" align="center" sx={{fontSize: '13px', marginTop: '14px'}}>
                {error}
              </Typography>
            )}


            {success && (
              <Typography color="success" variant="body2" align="center" sx={{fontSize: '13px', marginTop: '14px'}}>
                {success}
              </Typography>
            )}

            <SubmitButton
              type="button"
              fullWidth
              variant="contained"
              color="primary"
              onClick={fullyRegistered === null ? handleLogin : handleSubmit}
              sx={{
                borderRadius: '9px',
                background: 'linear-gradient(135deg, #3b7bf5 0%, #2557d0 100%)',
                boxShadow: '0 6px 12px rgba(47,111,237,0.2)',
                fontWeight: 600,
                fontSize: 13.5,
                textTransform: 'none',
                py: 0.9,
                '&:hover': { background: 'linear-gradient(135deg, #3570e6 0%, #1f4bb8 100%)' },
              }}
            >
              {fullyRegistered === null ? t('تحقق من حسابي') : t('دخول إلى حسابي')}
            </SubmitButton>

            <Button fullWidth size="small" onClick={() => navigate('/student/join')} sx={{ mt: 1.5, fontSize: 12, textTransform: 'none' }}>
              {t('Have a class invitation? Join here')}
            </Button>
          </Form>
          </PaperContainer>
        </CenteredContainer>
      </Box>
    </Box>
    </ThemeProvider>
  );
}
